import styled from '@emotion/styled'
import type { FC } from 'react'
import { SECONDARY_FONT_CSS_VAR } from '../../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../../style/typography'
import { SectionContainer, SectionTitle } from './Shared'
import { ScrollingLayout } from '../../../style/scrollingLayout'

export type RichTextHtmlSectionProps = {
    html: string
    sectionTitle?: string
    verticalPadding?: number
}

/**
 * Light DOM so Lexical HTML SSRs and renders with JS disabled.
 * Raw HTML sections use Shadow DOM — see HTMLSection.
 * Lexical persists headings/lists/links with Tailwind theme classes from
 *  `rich-text/lexicalTheme.ts`. Older DB rows often have plain semantic tags only;
 *  these baseline rules mirror that theme so storefront styling matches without
 *  requiring a re-save in admin.
 *
 *  When updating storefront typography, keep `lexicalTheme.ts` aligned with the
 *  heading scale and list/link/blockquote rules below.
 */
const RichTextHtmlBody = styled.div({
    width: '100%',
    alignSelf: 'stretch',
    overflowWrap: 'anywhere',

    // Force inheritance so the surrounding color reaches every element.
    // Each selector is (0,1,1) — enough to beat global element rules from
    // the embedding app or brand custom CSS. Headings keep their own font
    // rules below (same specificity, declared later, so they win).
    '&, & div, & span, & p, & ul, & ol, & li, & blockquote, & strong, & em, & h1, & h2, & h3, & h4, & h5, & h6':
        {
            fontFamily: 'inherit',
            color: 'inherit',
        },

    // Block-level body-text only. Spans, strong, em, and divs intentionally omitted: they inherit from their nearest role-wired ancestor, so a span inside an `<h2>` picks up `headingText` and a span inside an `<a>` picks up `linkText`.
    '&, & p, & ul, & ol, & li': {
        ...getTypographyRoleStyle('bodyText'),
    },

    '& h1': {
        ...getTypographyRoleStyle('displayHeading', {
            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
            fontWeight: 700,
            lineHeight: '2rem',
        }),
        fontSize: '1.5rem',
        margin: 0,
    },
    '& h2': {
        ...getTypographyRoleStyle('headingText', {
            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
            fontWeight: 700,
            lineHeight: '1.75rem',
        }),
        fontSize: '1.25rem',
        margin: 0,
    },
    '& h3': {
        ...getTypographyRoleStyle('headingText', {
            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
            fontWeight: 700,
            lineHeight: '1.75rem',
        }),
        fontSize: '1.125rem',
        margin: 0,
    },
    '& h4': {
        ...getTypographyRoleStyle('featureTitle', {
            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
            fontWeight: 700,
            lineHeight: '1.5rem',
        }),
        fontSize: '1rem',
        margin: 0,
    },
    '& h5': {
        ...getTypographyRoleStyle('featureTitle', {
            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
            fontWeight: 700,
            lineHeight: '1.25rem',
        }),
        fontSize: '0.875rem',
        margin: 0,
    },
    '& h6': {
        ...getTypographyRoleStyle('featureTitle', {
            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
            fontWeight: 700,
            lineHeight: '1rem',
        }),
        fontSize: '0.75rem',
        margin: 0,
    },

    '& p': {
        margin: '0.5em 0',
    },

    '& ul': {
        listStyleType: 'disc',
        listStylePosition: 'outside',
        paddingLeft: '1rem',
        margin: '0.5em 0',
    },
    '& ol': {
        listStyleType: 'decimal',
        listStylePosition: 'outside',
        paddingLeft: '1rem',
        margin: '0.5em 0',
    },
    '& li': {
        marginLeft: '1rem',
    },
    '& ul ul': {
        listStyleType: 'circle',
    },
    '& ul ul ul': {
        listStyleType: 'square',
    },

    '& a': {
        ...getTypographyRoleStyle('linkText'),
        color: 'rgb(37 99 235)',
        textDecoration: 'none',
    },
    '& a:hover': {
        textDecoration: 'underline',
        cursor: 'pointer',
    },

    '& blockquote': {
        borderLeft: '4px solid rgb(209 213 219)',
        paddingLeft: '1rem',
        paddingTop: '0.5rem',
        paddingBottom: '0.5rem',
        fontStyle: 'italic',
        margin: '0.5em 0',
    },

    '& hr': {
        border: 'none',
        borderTop: '1px solid rgb(209 213 219)',
        marginTop: '1rem',
        marginBottom: '1rem',
    },

    '& pre': {
        display: 'block',
        backgroundColor: 'rgb(243 244 246)',
        lineHeight: 1.625,
        padding: '0.5rem',
        margin: '0.5rem 0',
        overflowX: 'auto',
    },

    '& p code, & li code': {
        backgroundColor: 'rgb(243 244 246)',
        padding: '0.125rem 0.25rem',
        borderRadius: '0.25rem',
    },

    '& pre code': {
        backgroundColor: 'transparent',
        padding: 0,
        borderRadius: 0,
    },

    '& ::marker': {
        color: 'inherit',
    },
    '& li.list-none': {
        listStyleType: 'none',
    },
    '& li.list-none::before, & li.list-none::after': {
        display: 'none',
    },
})

export const RichTextHtmlSection: FC<RichTextHtmlSectionProps> = ({
    html,
    sectionTitle,
    verticalPadding,
}) => {
    let htmlString = html
    try {
        htmlString = decodeURIComponent(htmlString)
    } catch (_e) {}

    return (
        <SectionContainer
            verticalPadding={verticalPadding}
            maxContentWidth={ScrollingLayout.PROSE_MAX_WIDTH}
        >
            {sectionTitle && <SectionTitle>{sectionTitle}</SectionTitle>}
            <RichTextHtmlBody
                dangerouslySetInnerHTML={{ __html: htmlString }}
            />
        </SectionContainer>
    )
}
